/* ============================================================
   EZINK Buy 1 Get 1 Free cart add-on - assets/ez-bogo.js
   ------------------------------------------------------------
   Additive. Wraps EZCart render methods and, after the drawer
   paints, adds a BOGO block under the shipping meter and a FREE
   chip on the free lines (both carry data-ez-bogo-own and are
   rebuilt each pass), then patches two drawer-owned nodes:

     - the You save row, so all savings read on one line
     - the upsell heading, when one more item completes a pair

   Both patches stash the drawer's own text and record what was
   written, so running twice between renders cannot compound and
   the drawer gets its node back the moment the condition ends.
   Shipping Protection, the shipping meter, the quantity controls
   and the checkout form are untouched.

   Two sources of truth, in order:
   1. Shopify's allocation. Once the discount is live each line
      in /cart.js carries line_level_discount_allocations - the
      real answer to which product is free. Allocated amount /
      unit price = how many of that line's units are free, which
      is what the chip says. Matched by discount title so a
      customer code is never read as this promo. Zero-amount
      allocations (Shopify tags the qualifying line too) ignored.
   2. A local projection, before the discount exists: eligible
      units sorted by price desc, every second one free. Verified
      live - a $21 and a $39 item put the whole $21 on the
      cheaper line.

   You save contributes the projection ONLY while the discount is
   not applied. Once it is, the drawer's own figure already
   includes it and adding again double counts ($21 showed $42).
   ============================================================ */
(function () {
  'use strict';

  var CFG = window.__EZ_BOGO_CONFIG__ || {};
  if (CFG.enabled === false) return;

  var LABEL = CFG.label || 'Buy 1 Get 1 Free';
  var UPSELL_LABEL = CFG.upsellLabel || 'Add one more, get it free';
  var OWN = 'data-ez-bogo-own';
  var MATCH = (CFG.discountMatch || ['buy 1 get 1', 'bogo', 'b1g1'])
    .map(function (s) { return String(s).toLowerCase(); });

  /* ------------------------------------------------- money */
  /* No money helper is exported by the drawer, so the
     presentment format is derived: from CFG.moneySample (an
     amount put through Liquid's money filter), else from a
     price the drawer painted. AU, US, UK and ZA all differ. */
  var fmt = (function () {
    var cached = null;

    function parts(sample) {
      if (typeof sample !== 'string') return null;
      var m = sample.trim().match(/^([^0-9]*)([0-9][0-9., ’' ]*[0-9]|[0-9])([^0-9]*)$/);
      if (!m) return null;
      var body = m[2];
      var seps = body.replace(/[0-9]/g, '');
      var dec = seps.length ? seps.slice(-1) : '';
      var thou = seps.length > 1 ? seps.slice(-2, -1) : '';
      var decimals = 0;
      if (dec) {
        var tail = body.split(dec).pop();
        if (/^[0-9]+$/.test(tail)) decimals = tail.length;
      }
      /* three digits after the last separator = thousands mark */
      if (decimals === 3) { thou = dec; dec = ''; decimals = 0; }
      return { prefix: m[1], suffix: m[3], dec: dec, thou: thou, decimals: decimals };
    }

    return function () {
      if (cached) return cached;
      cached = parts(CFG.moneySample);
      if (cached) return cached;
      var n = document.querySelector('.ez-item__price, [data-ez-total], .ez-upsell__price');
      if (n) cached = parts(n.textContent);
      return cached;
    };
  })();

  function money(cents) {
    var f = fmt();
    if (!f) return (cents < 0 ? '-' : '') + (Math.abs(cents) / 100).toFixed(2);
    var out = (Math.abs(cents) / 100).toFixed(f.decimals).split('.');
    if (f.thou) out[0] = out[0].replace(/\B(?=([0-9]{3})+(?![0-9]))/g, f.thou);
    var body = out.length > 1 ? out[0] + (f.dec || '.') + out[1] : out[0];
    return (cents < 0 ? '-' : '') + f.prefix + body + f.suffix;
  }

  /* Inverse of money(), to read the drawer's own figure back
     out of the DOM. Returns cents, 0 if unreadable. */
  function parseMoney(text) {
    var m = String(text || '').match(/([0-9][0-9., ’' ]*[0-9]|[0-9])/);
    if (!m) return 0;
    var body = m[1];
    var seps = body.replace(/[0-9]/g, '');
    var dec = seps.length ? seps.slice(-1) : '';
    var decimals = 0;
    if (dec) {
      var tail = body.split(dec).pop();
      if (/^[0-9]+$/.test(tail)) decimals = tail.length;
    }
    if (decimals === 3) decimals = 0;
    var digits = body.replace(/[^0-9]/g, '');
    if (!digits) return 0;
    var n = parseInt(digits, 10);
    return decimals > 0 ? n : n * 100;
  }

  /* ------------------------------------------- eligibility */
  /* Mirrors snippets/bogo-eligible.liquid and the BOGO
     Eligible smart collection the discount runs on. */
  var EX = CFG.exclude || {};
  var EX_TITLE = (EX.titleContains || ['pack', 'mystery', 'bundle', 'custom', 'gift card', 'shipping protection', 'free tattoo'])
    .map(function (s) { return String(s).toLowerCase(); });
  var EX_SKU = (EX.skuPrefixes || ['CT-', 'MP-', 'GC-', 'SP-'])
    .map(function (s) { return String(s).toUpperCase(); });
  var EX_HANDLE = (EX.handles || []).map(function (s) { return String(s).toLowerCase(); });

  function eligible(line) {
    if (!line || line.gift_card) return false;
    if (!line.price || line.price <= 0) return false;

    var title = String(line.product_title || line.title || '').toLowerCase();
    var type = String(line.product_type || '').toLowerCase();
    var handle = String(line.handle || '').toLowerCase();
    var sku = String(line.sku || '').toUpperCase();
    var i;

    if (EX_HANDLE.indexOf(handle) !== -1) return false;
    for (i = 0; i < EX_TITLE.length; i++) {
      if (title.indexOf(EX_TITLE[i]) !== -1) return false;
      if (type && type.indexOf(EX_TITLE[i]) !== -1) return false;
    }
    for (i = 0; i < EX_SKU.length; i++) {
      if (sku.indexOf(EX_SKU[i]) === 0) return false;
    }
    /* Recharge bundle children are one grouped row in the drawer */
    if (line.properties && line.properties._rc_bundle_parent) return false;
    return true;
  }

  /* --------------------------------------- BOGO allocation */
  function bogoOnLine(line) {
    var amt = 0;
    var list = line.line_level_discount_allocations || [];
    for (var i = 0; i < list.length; i++) {
      var app = list[i].discount_application || list[i];
      var name = String(app.title || app.description || '').toLowerCase();
      for (var m = 0; m < MATCH.length; m++) {
        if (name.indexOf(MATCH[m]) !== -1) { amt += list[i].amount || 0; break; }
      }
    }
    return amt;
  }

  /* What Shopify actually did, or null before the discount exists */
  function allocated(cart) {
    var items = (cart && cart.items) || [];
    var byKey = {}, count = 0, amount = 0;

    for (var i = 0; i < items.length; i++) {
      var line = items[i];
      var amt = bogoOnLine(line);
      if (amt <= 0) continue;

      amount += amt;
      if (line.price > 0) {
        var n = Math.min(Math.round(amt / line.price), line.quantity);
        if (n > 0) { byKey[line.key] = (byKey[line.key] || 0) + n; count += n; }
      }
    }

    var cartLevel = (cart && cart.cart_level_discount_applications) || [];
    for (var c = 0; c < cartLevel.length; c++) {
      var name = String(cartLevel[c].title || cartLevel[c].description || '').toLowerCase();
      for (var k = 0; k < MATCH.length; k++) {
        if (name.indexOf(MATCH[k]) !== -1) { amount += cartLevel[c].total_allocated_amount || 0; break; }
      }
    }

    return amount > 0 ? { byKey: byKey, count: count, amount: amount } : null;
  }

  /* Projection, for before the discount is live */
  function project(cart) {
    var units = [];
    var items = (cart && cart.items) || [];
    var i;

    for (i = 0; i < items.length; i++) {
      if (!eligible(items[i])) continue;
      for (var q = 0; q < items[i].quantity; q++) {
        units.push({ key: items[i].key, price: items[i].price });
      }
    }
    units.sort(function (a, b) { return b.price - a.price; });

    var byKey = {}, saving = 0, count = 0;
    for (i = 1; i < units.length; i += 2) {
      byKey[units[i].key] = (byKey[units[i].key] || 0) + 1;
      saving += units[i].price;
      count++;
    }
    return { units: units.length, byKey: byKey, saving: saving, count: count };
  }

  /* ----------------------------------------------- helpers */
  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    n.setAttribute(OWN, '');
    return n;
  }

  function clearOwn(root) {
    var mine = root ? root.querySelectorAll('[' + OWN + ']') : [];
    for (var i = 0; i < mine.length; i++) {
      if (mine[i].parentNode) mine[i].parentNode.removeChild(mine[i]);
    }
  }

  /* Shared by the two drawer-owned nodes this file patches.
     Returns the drawer's own text, whether or not we wrote last. */
  function baseTextOf(node) {
    var text = node.textContent.trim();
    var patched = node.getAttribute('data-ez-bogo-patched');
    var ours = patched !== null && patched === text;
    return { text: text, ours: ours, base: ours ? (node.getAttribute('data-ez-bogo-base') || '') : text };
  }

  function restore(node, state) {
    if (!state.ours) return;
    node.textContent = state.base;
    node.removeAttribute('data-ez-bogo-patched');
    node.removeAttribute('data-ez-bogo-base');
  }

  function patch(node, state, value) {
    node.setAttribute('data-ez-bogo-base', state.base);
    node.textContent = value;
    node.setAttribute('data-ez-bogo-patched', value);
  }

  var GIFT_ICON =
    '<svg class="ez-bogo__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<rect x="3" y="8" width="18" height="13" rx="1.5"/><path d="M3 12h18M12 8v13"/>' +
    '<path d="M12 8S10.5 3 8 3a2.5 2.5 0 0 0 0 5zM12 8s1.5-5 4-5a2.5 2.5 0 0 1 0 5z"/></svg>';

  /* ------------------------------------------------ banner */
  function paintBanner(panel, s, cart) {
    if (!cart || !cart.item_count) return;

    var ship = panel.querySelector('[data-ez-ship]');
    var body = panel.querySelector('[data-ez-body]');
    if (!ship && !body) return;

    var msg, note;
    if (s.units % 2 === 1) {
      msg = 'Add 1 more tattoo and it’s <b>free</b>';
      note = 'Mix and match any designs. The cheaper one comes off.';
    } else if (s.freeCount > 0) {
      msg = '<b>' + s.freeCount + ' free tattoo' + (s.freeCount === 1 ? '' : 's') + '</b> unlocked';
      note = 'Add 1 more and you’ll get another one free.';
    } else {
      msg = LABEL + ' on all tattoos';
      note = 'Add any 2 tattoos and the cheaper one is on us.';
    }

    var unlocked = s.freeCount > 0 && s.units % 2 === 0;
    var block = el('div', unlocked ? 'ez-bogo ez-bogo--unlocked' : 'ez-bogo');
    block.setAttribute('data-ez-bogo', '');

    var row = el('div', 'ez-bogo__row');
    row.innerHTML = GIFT_ICON + '<div class="ez-bogo__msg">' + msg + '</div>';

    if (s.shown > 0) {
      var saved = document.createElement('span');
      saved.className = 'ez-bogo__saved';
      saved.textContent = money(-s.shown);
      row.appendChild(saved);
    }

    var track = el('div', 'ez-bogo__track');
    var fill = document.createElement('div');
    fill.className = 'ez-bogo__fill';
    fill.style.width = s.progress + '%';
    track.appendChild(fill);

    block.appendChild(row);
    block.appendChild(track);
    block.appendChild(el('div', 'ez-bogo__note', note));

    if (ship) ship.parentNode.insertBefore(block, ship.nextSibling);
    else body.parentNode.insertBefore(block, body);
  }

  /* ------------------------------------------- line chips */
  function paintChips(panel, s) {
    var list = panel.querySelector('[data-ez-items]');
    if (!list) return;

    var rows = list.querySelectorAll('[data-ez-line]');
    for (var i = 0; i < rows.length; i++) {
      var n = s.freeByKey[rows[i].getAttribute('data-ez-line')];
      if (!n) continue;

      var info = rows[i].querySelector('.ez-item__info');
      var title = rows[i].querySelector('.ez-item__title');
      if (!info || !title) continue;

      info.insertBefore(el('span', 'ez-bogo-chip', n + ' free'), title.nextSibling);
    }
  }

  /* ------------------------------------------ You save row */
  function mergeSaveRow(panel, s) {
    var row = panel.querySelector('[data-ez-save-row]');
    var val = panel.querySelector('[data-ez-save]');
    if (!row || !val) return;

    var st = baseTextOf(val);

    if (s.merge <= 0) { restore(val, st); return; }

    patch(val, st, money(-(parseMoney(st.base) + s.merge)));
    if (row.hidden) row.hidden = false;
  }

  /* ------------------------------------- upsell heading */
  /* With an odd number of eligible units the next add is free,
     and the upsell list is the thing that makes that add easy.
     Saying so there converts better than a generic heading.
     Restored the moment the count is even again. */
  function patchUpsellHead(panel, s) {
    var head = panel.querySelector('[data-ez-upsell-head]');
    if (!head) return;

    var st = baseTextOf(head);

    if (!(s.units > 0 && s.units % 2 === 1)) { restore(head, st); return; }

    patch(head, st, UPSELL_LABEL);
  }

  /* ------------------------------------------------- paint */
  var painting = false;

  function paint() {
    if (painting) return;
    var panel = document.querySelector('ez-cart-drawer .ez-panel');
    if (!panel) return;

    painting = true;
    try {
      var cart = window.EZCart && window.EZCart.cart;
      clearOwn(panel);

      var p = project(cart);
      var live = allocated(cart);
      var s = { units: p.units };

      if (live) {
        s.freeByKey = live.byKey;
        s.freeCount = live.count;
        s.shown = live.amount;
        s.merge = 0;
      } else {
        s.freeByKey = p.byKey;
        s.freeCount = p.count;
        s.shown = p.saving;
        s.merge = p.saving;
      }

      s.progress = (s.units > 0 && s.units % 2 === 1)
        ? 50
        : ((s.freeCount > 0 || s.units > 0) ? 100 : 0);

      paintBanner(panel, s, cart);
      paintChips(panel, s);
      mergeSaveRow(panel, s);
      patchUpsellHead(panel, s);
    } catch (e) {
      if (window.console && console.warn) console.warn('[ez-bogo]', e);
    } finally {
      painting = false;
    }
  }

  var queued = false;

  function schedule() {
    if (queued) return;
    queued = true;
    var run = function () { queued = false; paint(); };
    if (window.requestAnimationFrame) window.requestAnimationFrame(run);
    else window.setTimeout(run, 16);
  }

  /* -------------------------------------------------- hook */
  function wrap(obj, name) {
    var orig = obj[name];
    if (typeof orig !== 'function' || orig.__ezBogoWrapped) return;

    function wrapped() {
      var out = orig.apply(this, arguments);
      if (out && typeof out.then === 'function') out.then(schedule, schedule);
      else schedule();
      return out;
    }

    wrapped.__ezBogoWrapped = true;
    try { obj[name] = wrapped; } catch (e) { /* observer covers it */ }
  }

  function boot() {
    if (!window.EZCart) return false;

    wrap(window.EZCart, 'render');
    wrap(window.EZCart, 'renderItems');
    wrap(window.EZCart, 'renderFooter');
    wrap(window.EZCart, 'renderUpsells');
    wrap(window.EZCart, 'open');

    var list = document.querySelector('[data-ez-items]');
    if (list && window.MutationObserver) {
      new MutationObserver(schedule).observe(list, { childList: true });
    }

    schedule();
    return true;
  }

  if (!boot()) {
    var tries = 0;
    var timer = setInterval(function () {
      if (boot() || ++tries > 60) clearInterval(timer);
    }, 100);
  }
})();
